import EmailConstrain from "./constrains/EmailConstrain";
import StringConstrain from "./constrains/StringConstrain";
import IntegerConstrain from "./constrains/IntegerConstrain";
import FloatConstrain from "./constrains/FloatConstrain";
import BoolConstrain from "./constrains/BoolConstrain";
import DateTimeConstrain from "./constrains/DateTimeConstrain";
import NotBlankConstrain from "./constrains/NotBlankConstrain";
import NotBlankEnumConstrain from "./constrains/NotBlankEnumConstrain";

/**
 * class Transformer
 *
 * Wandelt die vom Server im JSON mitgelieferten Constrain-Definitionen
 * (type.constrains) in tatsächliche ConstrainBase-Instanzen um.
 *
 * Server-Format (Beispiel für ein E-Mail-Feld):
 * type: {
 *   ...
 *   constrains: {
 *     EmailConstrain: {
 *       message: "Email ist ungültig!",
 *       title: "Email ist ungültig!",
 *       _class: "po2\\types\\validator\\constrain\\EmailConstrain"
 *     },
 *     _class: "constrains"
 *   }
 * }
 *
 * D.h. "constrains" ist kein Array, sondern ein Objekt, das pro PHP-Klassenname
 * (Key) die serialisierten Eigenschaften des Constrains enthält. Der Key "_class"
 * auf oberster Ebene ist dabei nur ein Marker des Servers und kein Constrain.
 */
class Transformer {

  /**
   * Mapping vom kurzen PHP-Klassennamen (letzter Teil von _class) auf die
   * Factory-Funktion, welche daraus eine JS-Constrain-Instanz erzeugt.
   */
  static FACTORIES = {
    EmailConstrain: (raw) => new EmailConstrain(raw.message || raw.title || undefined),
    StringConstrain: (raw) => new StringConstrain(
      Transformer.toNumber(raw.min, 0),
      Transformer.toNumber(raw.max, -1)),
    IntegerConstrain: () => new IntegerConstrain(),
    FloatConstrain: () => new FloatConstrain(),
    BoolConstrain: () => new BoolConstrain(),
    DateTimeConstrain: () => new DateTimeConstrain(),
    NotBlankConstrain: () => new NotBlankConstrain(),
    NotBlankEnumConstrain: () => new NotBlankEnumConstrain()
  };

  static toNumber(value, defaultValue) {
    if (value === undefined || value === null || value === '') {
      return defaultValue;
    }
    const num = Number(value);
    return isNaN(num) ? defaultValue : num;
  }

  /**
   * Ermittelt den kurzen Klassennamen aus einem PHP-Klassenpfad (_class),
   * z.B. "po2\\types\\validator\\constrain\\EmailConstrain" -> "EmailConstrain".
   * @param {string} phpClass
   * @returns {string}
   */
  static getShortClassName(phpClass) {
    if (!phpClass) {
      return '';
    }
    const parts = String(phpClass).split('\\');
    return parts[parts.length - 1];
  }

  /**
   * Erzeugt aus einer einzelnen, vom Server gelieferten Constrain-Definition
   * die passende ConstrainBase-Instanz. Liefert null, falls keine JS-Entsprechung
   * bekannt ist.
   * @param {string} key  Der Objekt-Key innerhalb von type.constrains (i.d.R. bereits der Klassenname)
   * @param {object} raw  Die serialisierten Eigenschaften des Constrains
   * @returns {ConstrainBase|null}
   */
  static createConstrain(key, raw) {
    if (!raw || typeof raw !== 'object') {
      return null;
    }
    const shortClassName = Transformer.getShortClassName(raw._class) || key;
    const factory = Transformer.FACTORIES[shortClassName];
    if (!factory) {
      return null;
    }
    return factory(raw);
  }

  /**
   * Wandelt das komplette "constrains"-Objekt aus dem Server-JSON (type.constrains)
   * in ein Array von ConstrainBase-Instanzen um.
   * @param {object} rawConstrains  z.B. fieldType.constrains
   * @returns {ConstrainBase[]}
   */
  static transformConstrains(rawConstrains) {
    const constrains = [];
    if (!rawConstrains || typeof rawConstrains !== 'object') {
      return constrains;
    }
    for (let key in rawConstrains) {
      if (key === '_class') {
        continue;
      }
      const constrain = Transformer.createConstrain(key, rawConstrains[key]);
      if (constrain !== null) {
        constrains.push(constrain);
      }
    }
    return constrains;
  }
}

export default Transformer;
